import { useCallback, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "@firebase/storage";
import { v4 as generateUuid } from "uuid";
import { useUserContext } from "./userContext";
import { useUploadMediaFileMutation } from "./use-upload-media-file-mutation";
import { FirebasePaths, getFirebaseStoragePath, imageDb } from "../utils/firebase";
import { MediaFile } from "../__generated__/types";

export const useFirebaseFileUpload = (path: FirebasePaths) => {
  const { user } = useUserContext();
  const userId = user?.id ?? "unknown";
  const { uploadMediaFile } = useUploadMediaFileMutation();
  const [loading, setLoading] = useState(false);

  const onUpload = useCallback(
    async (files: FileList | null): Promise<MediaFile[]> => {
      if (!files || files.length === 0) return [];
      const results: MediaFile[] = [];
      setLoading(true);

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        if (!file) continue;
        const id = generateUuid();
        const storagePath = getFirebaseStoragePath(
          userId,
          `${path}/${id}` as FirebasePaths
        );
        const fileRef = ref(imageDb, storagePath);

        try {
          const uploadResult = await uploadBytes(fileRef, file, {
            contentType: file.type || "application/octet-stream",
          });
          const url = await getDownloadURL(uploadResult.ref);
          const mediaFile = await uploadMediaFile({
            userId,
            url,
            path: uploadResult.metadata.fullPath,
            title: file.name,
          });
          if (mediaFile) {
            results.push(mediaFile as MediaFile);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }

      setLoading(false);
      return results;
    },
    [path, uploadMediaFile, userId]
  );

  return { onUpload, loading };
};
